import * as React from 'react';
import { Form, Input, Checkbox, Button, Row } from 'antd';
import { Formik, FormikProps } from "formik"
import * as Yup from 'yup';
import { useCookies } from 'react-cookie';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faLockAlt
} from '@fortawesome/pro-light-svg-icons';
import { useRef } from 'react';
import { useEffect } from 'react';


interface LoginFormFields {
    UserName: string;
    Password: string;
    RememberMe: boolean;
}

function useLoginForm(onSubmit) {
    const [cookies] = useCookies();
    const handleSubmit = React.useCallback(
        (loginFormFields: LoginFormFields) => {
            onSubmit(loginFormFields);
        },
        [],
    );
    const initialValues: LoginFormFields = {
        UserName: cookies.username,
        Password: '',
        RememberMe: false,
    };

    const validationSchema =
        Yup.object().shape({
            UserName: Yup.string().trim()
                .email('Please enter a valid email address.')
                .required('Email address is required.'),
            Password: Yup.string()
                .required('Password is required.'),
        });

    return {
        handleSubmit,
        initialValues,
        validationSchema
    };
}

const LoginForm = ({onSubmit, handleForgot, isInputDisabled, isAutoFocus, formRef}) => {
    const inputRef = useRef<Input>();
    
    useEffect(() => {
        if (isAutoFocus) {
            inputRef.current.focus();
        }
    }, [isAutoFocus])

    const { initialValues, handleSubmit, validationSchema } = useLoginForm(onSubmit);
    const suffix = (
        <Button id="ForgotPasswordButton" type="link" onClick={handleForgot}>Forgot?</Button>
    )

    return (
        <Formik
            innerRef={formRef}
            enableReinitialize
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}
        >
            {({
                values,
                touched,
                setFieldValue,
                handleChange,
                handleBlur,
                isValid,
                handleSubmit,
                errors,
            }) => (
                    <Form
                        onFinish={handleSubmit}
                        initialValues={initialValues}
                    >
                        <Form.Item
                            help={((touched.UserName && errors.UserName) ? errors.UserName : null)}
                            validateStatus={(touched.UserName && errors.UserName) ? 'error' : ''}

                        >
                            <Input
                                name="UserName"
                                size="large"
                                prefix={<FontAwesomeIcon icon={faUser} />}
                                placeholder="Email"
                                value={values.UserName}
                                onChange={e => setFieldValue("UserName", e.target.value?.trim())}
                                onBlur={handleBlur} 
                                ref={inputRef}
                                disabled={isInputDisabled}
                            />
                        </Form.Item>
                        <Form.Item
                            name="Password"
                            help={((touched.Password && errors.Password) ? errors.Password : null)}
                            validateStatus={(touched.Password && errors.Password) ? 'error' : ''}
                        >
                            <Input.Password
                                className="input-password"
                                size="large" 
                                prefix={<FontAwesomeIcon icon={faLockAlt} />}
                                addonAfter={suffix}
                                placeholder="Password"
                                value={values.Password}
                                onChange={handleChange}
                                onBlur={handleBlur} />
                        </Form.Item>

                        <Row justify="space-between" align="middle">
                            <Form.Item name="RememberMe" valuePropName="checked">
                                <Checkbox onChange={handleChange}>Remember me</Checkbox>
                            </Form.Item>
                            <Form.Item>
                                <Button id="SignInButton" type="primary" htmlType="submit">
                                    Sign In
                                </Button>
                            </Form.Item>
                        </Row>

                    </Form>
                )}
        </Formik>
    )
}

export default LoginForm;
